/**
 * The document repository's visual vocabulary — ONE source for the tokens,
 * the type scale and the controls every DMS screen renders with.
 *
 * Why this file exists at all:
 *
 * 1. **Token drift.** Every screen in here used to reach for `--color-primary`,
 *    `--color-surface` and `--color-danger`, none of which the platform theme
 *    defined, so each `var(--x, #fallback)` silently painted its fallback and
 *    the repository was the one part of the product that was not brand blue.
 *    Those names are now aliased in `appTheme.ts`, and everything here uses the
 *    CANONICAL tokens so the mistake cannot be made again by copy-paste.
 *
 * 2. **Type scale.** The repository drew body text at 13px and metadata at 11px
 *    against the application's 15px base — on the densest, longest-read screen
 *    in the product. `TYPE` is the floor: 15px for anything you read, 14px for
 *    supporting lines, 13px as the smallest permitted size. Nothing in the DMS
 *    should hard-code a font size that is not one of these.
 *
 * 3. **Controls.** Each screen grew its own `btnStyle` (5px 10px, radius 4)
 *    while the rest of the app used `.btn` (10px 20px, radius 8, gradient
 *    primary). These objects are the `.btn` family expressed as inline styles,
 *    because the shared views render inside hosts that do not all load
 *    globals.css — the standalone runtime injects `appTheme.ts` instead.
 */

import type { CSSProperties } from 'react'

/**
 * Canonical theme tokens, each with a LITERAL light-theme fallback.
 *
 * The fallback is not decoration. These components render in hosts that do not
 * all load `globals.css` — the standalone runtime injects `appTheme.ts`, and a
 * preview frame may load neither — and a bare `var(--color-text)` in a host
 * that never defined it resolves to nothing: an input with no background and
 * inherited text, which is how the search box ended up invisible. Same
 * convention as `RunNavShell`: platform and runtime get the theme-aware value,
 * anything else gets a coherent light palette rather than a hole.
 *
 * Use these, never a raw hex and never an alias.
 */
export const T = {
  bg: 'var(--color-bg, #f4f6f9)',
  surface: 'var(--color-bg-secondary, #ffffff)',
  surface2: 'var(--color-surface-2, #f0f3f7)',
  raised: 'var(--color-raised, #ffffff)',
  border: 'var(--color-border, #dde3ea)',
  borderStrong: 'var(--color-border-strong, #c7cfd9)',
  text: 'var(--color-text, #1c2430)',
  textSecondary: 'var(--color-text-secondary, #465062)',
  textFaint: 'var(--color-text-faint, #687281)',
  accent: 'var(--color-accent, #0061D0)',
  accentWeak: 'var(--color-accent-weak, rgba(0, 97, 208, 0.10))',
  success: 'var(--color-success, #1a7f37)',
  successWeak: 'var(--color-success-weak, rgba(26, 127, 55, 0.10))',
  warning: 'var(--color-warning, #9a6700)',
  warningWeak: 'var(--color-warning-weak, rgba(154, 103, 0, 0.10))',
  error: 'var(--color-error, #cf222e)',
  errorWeak: 'var(--color-error-weak, rgba(207, 34, 46, 0.10))',
  info: 'var(--color-info, #0969da)',
  infoWeak: 'var(--color-info-weak, rgba(9, 105, 218, 0.10))',
  /* The control background specifically: an input that inherits the panel
     colour is an input nobody can see they have focused. */
  inputBg: 'var(--color-input-bg, #ffffff)',
  shadow: 'var(--color-shadow, rgba(80, 90, 110, 0.18))',
  mono: 'var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)',
} as const

/**
 * The permitted sizes. `small` is a FLOOR, not a default — reach for it only
 * for a genuinely secondary line, never for something a reader has to act on.
 */
export const TYPE = {
  /** Screen title. */
  title: 20,
  /** Section heading, and the title of a document in a list. */
  heading: 16,
  /** Anything a person reads. */
  body: 15,
  /** The line under a title: what the status means, who owns it. */
  meta: 14,
  /** Timestamps, counts, field labels. Nothing smaller exists. */
  small: 13,
} as const

export const RADIUS = { sm: 6, md: 8, lg: 12, full: 9999 } as const

// ── tone ────────────────────────────────────────────────────────────────────

/** The five meanings a chip, dot or number can carry. */
export type Tone = 'neutral' | 'ok' | 'warn' | 'bad' | 'accent'

export function toneColor(tone: Tone): string {
  switch (tone) {
    case 'ok': return T.success
    case 'warn': return T.warning
    case 'bad': return T.error
    case 'accent': return T.accent
    default: return T.textSecondary
  }
}

export function toneWash(tone: Tone): string {
  switch (tone) {
    case 'ok': return T.successWeak
    case 'warn': return T.warningWeak
    case 'bad': return T.errorWeak
    case 'accent': return T.accentWeak
    default: return T.surface2
  }
}

// ── controls ────────────────────────────────────────────────────────────────

/** `.btn` + `.btn-sm`, as inline styles. */
export const btnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '6px 12px',
  fontSize: TYPE.small,
  fontWeight: 500,
  lineHeight: 1.2,
  fontFamily: 'inherit',
  border: `1px solid ${T.border}`,
  borderRadius: RADIUS.md,
  background: T.surface,
  color: T.text,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
}

/** `.btn-primary` — the brand gradient, identical in both themes. */
export const btnPrimaryStyle: CSSProperties = {
  ...btnStyle,
  padding: '7px 14px',
  fontSize: TYPE.meta,
  fontWeight: 600,
  border: 'none',
  backgroundImage: 'linear-gradient(90deg, var(--gr-drkblue), var(--gr-drkgreen))',
  color: '#ffffff',
}

/** A button that reads as the destructive/returning choice. */
export const btnDangerStyle: CSSProperties = {
  ...btnStyle,
  borderColor: T.error,
  color: T.error,
}

export const inputStyle: CSSProperties = {
  padding: '7px 10px',
  fontSize: TYPE.meta,
  fontFamily: 'inherit',
  border: `1px solid ${T.border}`,
  borderRadius: RADIUS.md,
  background: T.inputBg,
  color: T.text,
}

/** The one big search field at the top of the library. */
export const searchStyle: CSSProperties = {
  ...inputStyle,
  padding: '10px 14px',
  fontSize: TYPE.body,
  borderColor: T.borderStrong,
  borderRadius: RADIUS.lg,
}

export const linkStyle: CSSProperties = {
  border: 'none',
  background: 'none',
  padding: 0,
  cursor: 'pointer',
  color: T.accent,
  fontSize: 'inherit',
  fontFamily: 'inherit',
  textAlign: 'left',
}

export const cardStyle: CSSProperties = {
  border: `1px solid ${T.border}`,
  borderRadius: RADIUS.lg,
  background: T.surface,
  padding: 16,
}

/** A row in the library: a card you can click, not a table cell. */
export const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '14px 16px',
  border: `1px solid ${T.border}`,
  borderRadius: RADIUS.md,
  background: T.surface,
}

// ── text ────────────────────────────────────────────────────────────────────

export const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: TYPE.title,
  fontWeight: 600,
  letterSpacing: '-0.02em',
  color: T.text,
}

export const headingStyle: CSSProperties = {
  margin: 0,
  fontSize: TYPE.heading,
  fontWeight: 600,
  color: T.text,
}

/** The line under a title that says what the status MEANS. */
export const explainStyle: CSSProperties = {
  margin: 0,
  fontSize: TYPE.meta,
  lineHeight: 1.5,
  color: T.textSecondary,
}

export const metaStyle: CSSProperties = {
  fontSize: TYPE.small,
  color: T.textFaint,
}

export const mutedStyle: CSSProperties = {
  fontSize: TYPE.small,
  color: T.textSecondary,
}

export const mutedBlockStyle: CSSProperties = {
  padding: 24,
  fontSize: TYPE.body,
  lineHeight: 1.55,
  color: T.textSecondary,
}

export const sectionTitleStyle: CSSProperties = {
  margin: '0 0 8px',
  fontSize: TYPE.small,
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: T.textFaint,
}

export const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: TYPE.small,
  fontWeight: 500,
  color: T.textSecondary,
  marginBottom: 5,
}

export const errStyle: CSSProperties = {
  margin: '8px 0',
  fontSize: TYPE.meta,
  lineHeight: 1.5,
  color: T.error,
}

// ── containers ──────────────────────────────────────────────────────────────

export const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 16px',
  borderBottom: `1px solid ${T.border}`,
  background: T.surface,
  flexWrap: 'wrap',
}

export const footerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 16px',
  fontSize: TYPE.small,
  color: T.textFaint,
  borderTop: `1px solid ${T.border}`,
}

export const scrollAreaStyle: CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
}
